import React from 'react';
import { useTranslation } from 'react-i18next';
import Search from '@/icons/Search';
import Close from '@/icons/Close';
import MemberFilterTypeahead, { type MemberFilterOption } from '@/components/shared/MemberFilterTypeahead';
import MemoriesSortControls from '@/components/memories/MemoriesSortControls';
import type { MemoriesFilters } from '@/types/memories';
import { hasActiveMemoriesFilters } from '@/types/memories';

import './memories-filter-bar.css';

export type { MemberFilterOption };

interface MemoriesFilterBarProps {
  filters: MemoriesFilters;
  members: MemberFilterOption[];
  authors: MemberFilterOption[];
  onFiltersChange: (filters: MemoriesFilters) => void;
  onSearch: () => void;
}

const MemoriesFilterBar: React.FC<MemoriesFilterBarProps> = ({
  filters,
  members,
  authors,
  onFiltersChange,
  onSearch
}) => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const active = hasActiveMemoriesFilters(filters);

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      query: '',
      familyMemberId: null,
      authorUserId: null,
      hasAttachments: null
    });
    onSearch();
  };

  return (
    <div className="memories-filter-bar">
      <div className="memories-filter-bar__row list-search-row">
        <div className="list-search-row__start">
          <div className="list-search-field">
            <span className="list-search-field__icon">
              <Search width={16} height={16} />
            </span>
            <input
              type="text"
              className="list-search-field__input"
              placeholder={t('memoriesFilter.searchPlaceholder')}
              value={filters.query}
              onChange={(e) => onFiltersChange({ ...filters, query: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
              aria-label={t('memoriesFilter.searchAria')}
            />
            {filters.query && (
              <button
                type="button"
                className="list-search-field__clear"
                onClick={() => {
                  onFiltersChange({ ...filters, query: '' });
                  onSearch();
                }}
                aria-label={tForms('actions.clearSearch')}
              >
                <Close width={14} height={14} />
              </button>
            )}
          </div>

          <MemberFilterTypeahead
            id="memories-member-filter"
            value={filters.familyMemberId}
            members={members}
            maxResults={5}
            placeholder={t('memoriesFilter.aboutMember')}
            ariaLabel={t('memoriesFilter.aboutMemberAria')}
            onChange={(familyMemberId) => {
              onFiltersChange({ ...filters, familyMemberId });
              onSearch();
            }}
          />

          <MemberFilterTypeahead
            id="memories-author-filter"
            value={filters.authorUserId}
            members={authors}
            maxResults={5}
            placeholder={t('memoriesFilter.author')}
            ariaLabel={t('memoriesFilter.authorAria')}
            onChange={(authorUserId) => {
              onFiltersChange({ ...filters, authorUserId });
              onSearch();
            }}
          />

          <button
            type="button"
            className={`list-filter-toggle${filters.hasAttachments ? ' list-filter-toggle--active' : ''}`}
            aria-pressed={!!filters.hasAttachments}
            onClick={() => {
              onFiltersChange({
                ...filters,
                hasAttachments: filters.hasAttachments ? null : true
              });
              onSearch();
            }}
          >
            {t('memoriesFilter.hasMedia')}
          </button>

          <button type="button" className="btn btn--secondary btn--sm" onClick={onSearch}>
            {tCommon('search')}
          </button>
        </div>

        <MemoriesSortControls
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSortChange={(sortBy, sortOrder) => {
            onFiltersChange({ ...filters, sortBy, sortOrder });
            onSearch();
          }}
        />
      </div>

      {active && (
        <div className="memories-filter-bar__chips">
          {filters.query.trim() && <span className="memories-filter-bar__chip">“{filters.query.trim()}”</span>}
          {filters.familyMemberId && (
            <span className="memories-filter-bar__chip">
              {t('memoriesFilter.aboutChip', {
                name: members.find((m) => m.id === filters.familyMemberId)?.label ?? t('memoriesFilter.memberFallback')
              })}
            </span>
          )}
          {filters.authorUserId && (
            <span className="memories-filter-bar__chip">
              {t('memoriesFilter.authorChip', {
                name: authors.find((a) => a.id === filters.authorUserId)?.label ?? t('memoriesFilter.authorFallback')
              })}
            </span>
          )}
          {filters.hasAttachments && <span className="memories-filter-bar__chip">{t('memoriesFilter.hasMedia')}</span>}
          <button type="button" className="memories-filter-bar__clear" onClick={clearFilters}>
            <Close width={12} height={12} />
            {tForms('actions.clearFilters')}
          </button>
        </div>
      )}
    </div>
  );
};

export default MemoriesFilterBar;
